import styled from 'styled-components'

import theme from 'theme_'
import { ButtonVariants, CommonButtonProps } from './CommonButton'
import { CommonLinkButtonProps } from './CommonLinkButton'
import { Font14, Font16 } from 'components/Typography'

interface GeneratedColors {
  color: string
  colorHover: string
  border: string
  borderHover: string
  background: string
  backgroundHover: string
  fontWeight?: string
}

const generateColors = (
  disabled: boolean | undefined,
  variant?: ButtonVariants
): GeneratedColors => {
  const filledDisabled = {
    color: theme.colors.white,
    colorHover: 'none',
    border: 'none',
    borderHover: 'none',
    background: theme.colors.darkeyGrey,
    backgroundHover: 'transparent',
  }

  const outlinedDisabled = {
    color: theme.colors.disabledGrey,
    colorHover: theme.colors.disabledGrey,
    border: `1px solid ${theme.colors.disabledGrey}`,
    borderHover: `1px solid ${theme.colors.disabledGrey}`,
    background: 'transparent',
    backgroundHover: 'transparent',
  }

  // TODO: need fix - primary button is always 2px smaller then secondary because of border
  switch (variant) {
    case 'errorFilled':
      return {
        color: theme.colors.white,
        colorHover: theme.colors.white,
        border: 'none',
        borderHover: 'none',
        background: theme.colors.error,
        backgroundHover: theme.colors.duneWarmGrey,
      }
    case 'primary':
      return disabled
        ? filledDisabled
        : {
            color: theme.colors.white,
            colorHover: theme.colors.white,
            border: 'none',
            borderHover: 'none',
            background: theme.colors.horizonOrange,
            backgroundHover: theme.colors.horizonOrange,
          }
    case 'secondary':
      return disabled
        ? outlinedDisabled
        : {
            color: theme.colors.horizonOrange,
            colorHover: theme.colors.horizonOrange,
            border: `1px solid ${theme.colors.horizonOrange}`,
            borderHover: `1px solid ${theme.colors.horizonOrange}`,
            background: theme.colors.white,
            backgroundHover: theme.colors.white,
          }
    default:
      return disabled
        ? outlinedDisabled
        : {
            color: theme.colors.horizonOrange,
            colorHover: theme.colors.horizonOrange,
            border: `1px solid ${theme.colors.pelicanGrey}`,
            borderHover: `1px solid ${theme.colors.pelicanGrey}`,
            background: 'transparent',
            backgroundHover: 'transparent',
          }
  }
}

export const ButtonPaddings = {
  small: '8px 16px',
  big: '16px 32px',
  DEFAULT: '12px 32px',
}

const Font = {
  small: Font14,
  big: Font16,
  DEFAULT: Font16,
}

export const StyledCommonButton = styled.button<CommonButtonProps>`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  width: fit-content;
  padding: ${(p) => (p.size ? ButtonPaddings[p.size] : ButtonPaddings.DEFAULT)};
  ${(p) => (p.size ? Font[p.size] : Font.DEFAULT)};
  font-family: ${(p) => p.theme.fonts.base};
  color: ${(p) => generateColors(p.disabled, p.variant).color};
  border: ${(p) => generateColors(p.disabled, p.variant).border};
  background-color: ${(p) => generateColors(p.disabled, p.variant).background};
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;

  :hover {
    color: ${(p) => generateColors(p.disabled, p.variant).colorHover};
    border: ${(p) => generateColors(p.disabled, p.variant).borderHover};
    background-color: ${(p) =>
      generateColors(p.disabled, p.variant).backgroundHover};
  }

  :focus-visible {
    outline: 1px solid ${(p) => p.theme.colors.horizonOrange};
  }

  :disabled {
    pointer-events: none;
  }
`

export const StyledCommonLinkButton = styled.button<CommonLinkButtonProps>`
  color: ${(p) => p.theme.colors.horizonOrange};
  font-weight: bold;
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0 6px;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
